import { Slider } from '@mui/material';
import * as R from 'ramda';
import { useEffect, useState } from 'react';

import { AnalyticsService } from '@/services';
import {
  playerThunks,
  selectActiveChapterId,
  selectActiveEpisodeId,
  selectEpisodeCompliance,
  selectIsPlayerSeeking,
  selectIsTrackLoaded,
  selectPlayerProgress,
  selectPlayerSeekToPosition,
  useAppDispatch,
  useAppSelector,
  useFetchChapterQuery,
} from '@/store';
import { isChapterCompleted } from '@/store/player/player.utils';
import { Referrer } from '@/types';

export const ProgressBar = ({ referrer }: { referrer: Referrer }) => {
  const dispatch = useAppDispatch();
  const [sliderValue, setSliderValue] = useState<number>();
  const [isSliding, setIsSliding] = useState(false);

  const activeChapterId = useAppSelector(selectActiveChapterId)!;
  const activeEpisodeId = useAppSelector(selectActiveEpisodeId)!;
  const compliance = useAppSelector(state =>
    selectEpisodeCompliance(state, activeEpisodeId),
  );

  const { data: chapter } = useFetchChapterQuery({
    chapterId: activeChapterId,
    compliance,
  });

  const location = referrer;
  const playerProgress = useAppSelector(selectPlayerProgress);
  const seekToPosition = useAppSelector(selectPlayerSeekToPosition);
  const isPlayerSeeking = useAppSelector(selectIsPlayerSeeking);
  const isTrackLoaded = useAppSelector(selectIsTrackLoaded);

  useEffect(() => {
    if (
      !isSliding &&
      !isPlayerSeeking &&
      !R.isNil(playerProgress.position) &&
      sliderValue !== playerProgress.position
    ) {
      setSliderValue(playerProgress.position);
    }
  }, [isPlayerSeeking, playerProgress, sliderValue, isSliding]);

  const onSlide = (val: number) => {
    setSliderValue(val);
    setIsSliding(true);
  };

  const onSlidingComplete = (val: number) => {
    dispatch(playerThunks.seekToPosition({ position: val }));
    AnalyticsService.trackPlayerSeek({
      chapterId: activeChapterId,
      episodeId: activeEpisodeId,
      seekStart: playerProgress.position!,
      seekLength: val - playerProgress.position!,
      alreadyListened: !!(
        chapter &&
        isChapterCompleted({
          currentTime: playerProgress.position!,
          duration: chapter.duration,
        })
      ),
      location,
    });
    setIsSliding(false);
  };

  const position =
    sliderValue ||
    (isPlayerSeeking ? seekToPosition : playerProgress.position) ||
    0;

  return (
    <Slider
      value={position}
      max={chapter?.duration}
      aria-label="Seek position"
      onChange={(_e, val) => onSlide(val as number)}
      onChangeCommitted={(_e, val) => onSlidingComplete(val as number)}
      sx={{
        ...styles.root,
        '& .MuiSlider-thumb': styles.thumb,
        '& .MuiSlider-track': styles.track,
        '& .MuiSlider-rail': styles.rail,
      }}
      disabled={!isTrackLoaded}
    />
  );
};

const styles = {
  root: {
    display: 'flex',
    boxSizing: 'border-box',
    borderRadius: 0,
    p: { xs: 0 },
    height: 4,
  },
  track: {
    color: 'secondary.active',
    height: 4,
    transition: 'width 0.3s ease',
  },
  rail: {
    color: 'white',
    height: 4,
  },
  thumb: {
    color: 'secondary.active',
    height: 12,
    width: 12,
    transition: 'left 0.3s ease',
  },
};
